import * as angular from 'angular';
import { Inject, Injectable, Optional, SkipSelf } from "@angular/core";
import { downgradeInjectable } from "@angular/upgrade/static";

import { HttpClient } from "@angular/common/http";
import { Observable } from 'rxjs';

import { AccesControleSecurite } from '../entities/acces-controle-securite.entity';
import { AccesEtat } from '../entities/acces-etat.entity';
import { IndicatifTelephone } from '../entities/indicatif-telephone.entity';
import { IntervenantRole } from '../entities/intervenant-role.entity';
import { MemoCategory } from '../entities/memo-category.entity';
import { MemoSubCategory } from '../entities/memo-sub-category.entity';
import { MemoLevel } from '../entities/memo-level.entity';
import { NiveauAlerteIncendie } from '../entities/niveau-alerte-incendie.entity';
import { OperationEtat } from '../entities/operation-etat.entity';
import { ParcMaintenance } from '../entities/parc-maintenance.entity';
import { ParcTechnologie } from '../entities/parc-technologie.entity';
import { TypeAcces } from '../entities/type-acces.entity';

import { BasicDataSubService } from '../../shared/basic-data-management/basic-data-subservice.class';
import { DocumentPaSteps } from '../entities/document-pa-steps';

@Injectable({ providedIn: 'root' })
export class StaticTypeService {
	private path: string;
	private accesControlesSecuriteSubService!: BasicDataSubService<AccesControleSecurite>;
	private accesEtatSubService!: BasicDataSubService<AccesEtat>;
	private indicatifTelephoneSubService!: BasicDataSubService<IndicatifTelephone>;
	private intervenantRoleSubService!: BasicDataSubService<IntervenantRole>;
	private memoCategorySubService!: BasicDataSubService<MemoCategory>;
	private memoSubCategorySubService!: BasicDataSubService<MemoSubCategory>;
	private memoLevelSubService!: BasicDataSubService<MemoLevel>;
	private niveauAlerteIncendieSubService!: BasicDataSubService<NiveauAlerteIncendie>;
	private operationEtatSubService!: BasicDataSubService<OperationEtat>;
	private parcMaintenanceSubService!: BasicDataSubService<ParcMaintenance>;
	private parcTechnologieSubService!: BasicDataSubService<ParcTechnologie>;
	private typeAccesSubService!: BasicDataSubService<TypeAcces>;
	private documentPaStepsSubService!: BasicDataSubService<DocumentPaSteps>;

	constructor( //
		@Inject(HttpClient) httpClient: HttpClient, //
		@Optional() @SkipSelf() previousInstance?: StaticTypeService //
	) {
		if (previousInstance) {
			throw new Error(
				'StaticTypeService is already loaded. Import it in the AppModule only');
		}
		this.path = (window as any).baseApiPath;

		this.accesControlesSecuriteSubService = new BasicDataSubService<AccesEtat>(httpClient, this.path + 'acces-controles-securite');
		this.accesEtatSubService = new BasicDataSubService<AccesEtat>(httpClient, this.path + 'acces-etat');
		this.indicatifTelephoneSubService = new BasicDataSubService<IndicatifTelephone>(httpClient, this.path + 'indicatifs-telephone');
		this.intervenantRoleSubService = new BasicDataSubService<IntervenantRole>(httpClient, this.path + 'intervenant-roles');
		this.memoCategorySubService = new BasicDataSubService<MemoCategory>(httpClient, this.path + 'memo-categories');
		this.memoSubCategorySubService = new BasicDataSubService<MemoSubCategory>(httpClient, this.path + 'memo-sub-categories');
		this.memoLevelSubService = new BasicDataSubService<MemoLevel>(httpClient, this.path + 'memo-levels');
		this.niveauAlerteIncendieSubService = new BasicDataSubService<NiveauAlerteIncendie>(httpClient, this.path + 'niveaux-alerte-incendie');
		this.operationEtatSubService = new BasicDataSubService<OperationEtat>(httpClient, this.path + 'operation-etat');
		this.parcMaintenanceSubService = new BasicDataSubService<ParcMaintenance>(httpClient, this.path + 'parc-maintenances');
		this.parcTechnologieSubService = new BasicDataSubService<ParcTechnologie>(httpClient, this.path + 'parc-technologies');
		this.typeAccesSubService = new BasicDataSubService<TypeAcces>(httpClient, this.path + 'type-acces');
		this.documentPaStepsSubService = new BasicDataSubService<DocumentPaSteps>(httpClient, this.path + 'document-pa-steps');
	}

	// Fonctions pour le type statique AccesControleSecurite
	public getAccesControleSecuriteList(): Observable<AccesControleSecurite[]> {
		return this.accesControlesSecuriteSubService.getList();
	}
	public getAccesControleSecuriteMapById(): Observable<Map<number, AccesControleSecurite>> {
		return this.accesControlesSecuriteSubService.getMapById();
	}
	
	// Fonctions pour le type statique AccesEtat
	public getAccesEtatList(): Observable<AccesEtat[]> {
		return this.accesEtatSubService.getList();
	}
	public getAccesEtatMapById(): Observable<Map<number, AccesEtat>> {
		return this.accesEtatSubService.getMapById();
	}

	// Fonctions pour le type statique IndicatifTelephone
	public getIndicatifTelephoneList(): Observable<IndicatifTelephone[]> {
		return this.indicatifTelephoneSubService.getList();
	}

	// Fonctions pour le type statique IntervenantRole
	public getIntervenantRoleList(): Observable<IntervenantRole[]> {
		return this.intervenantRoleSubService.getList();
	}
	public getIntervenantRoleMapById(): Observable<Map<number, IntervenantRole>> {
		return this.intervenantRoleSubService.getMapById();
	}

	// Fonctions pour le type statique MemoCategory
	public getMemoCategoryList(): Observable<MemoCategory[]> {
		return this.memoCategorySubService.getList();
	}
	public getMemoCategoryMapById(): Observable<Map<number, MemoCategory>> {
		return this.memoCategorySubService.getMapById();
	}

	// Fonctions pour le type statique MemoSubCategory
	public getMemoSubCategoryList(): Observable<MemoSubCategory[]> {
		return this.memoSubCategorySubService.getList();
	}
	public getMemoSubCategoryMapById(): Observable<Map<number, MemoSubCategory>> {
		return this.memoSubCategorySubService.getMapById();
	}
	public getMemoSubCategoryMap(): Observable<Map<number, MemoSubCategory[]>> {
		return this.memoSubCategorySubService.getMapByForeignKey('parent', (entity: MemoSubCategory) => entity.parentId);
	}
	public getMemoSubCategoriesByParent(parentId: number): Observable<MemoSubCategory[]> {
		return this.memoSubCategorySubService.getByForeignKey('parent', (entity: MemoSubCategory) => entity.parentId, parentId);
	}

	// Fonctions pour le type statique MemoLevel
	public getMemoLevelList(): Observable<MemoLevel[]> {
		return this.memoLevelSubService.getList();
	}
	public getMemoLevelMapById(): Observable<Map<number, MemoLevel>> {
		return this.memoLevelSubService.getMapById();
	}

	// Fonctions pour le type statique NiveauAlerteIncendie
	public getNiveauAlerteIncendieList(): Observable<NiveauAlerteIncendie[]> {
		return this.niveauAlerteIncendieSubService.getList();
	}
	public getNiveauAlerteIncendieMapById(): Observable<Map<number, NiveauAlerteIncendie>> {
		return this.niveauAlerteIncendieSubService.getMapById();
	}
	
	// Fonctions pour le type statique OperationEtat
	public getOperationEtatList(): Observable<OperationEtat[]> {
		return this.operationEtatSubService.getList();
	}
	public getOperationEtatMapById(): Observable<Map<number, OperationEtat>> {
		return this.operationEtatSubService.getMapById();
	}

	// Fonctions pour le type statique ParcMaintenance
	public getParcMaintenanceList(): Observable<ParcMaintenance[]> {
		return this.parcMaintenanceSubService.getList();
	}
	public getParcMaintenanceMapById(): Observable<Map<number, MemoLevel>> {
		return this.parcMaintenanceSubService.getMapById();
	}

	// Fonctions pour le type statique ParcTechnologie
	public getParcTechnologieList(): Observable<ParcTechnologie[]> {
		return this.parcTechnologieSubService.getList();
	}
	public getParcTechnologieMapById(): Observable<Map<number, ParcTechnologie>> {
		return this.parcTechnologieSubService.getMapById();
	}

	// Fonctions pour le type statique TypeAcces
	public getTypeAccesList(): Observable<TypeAcces[]> {
		return this.typeAccesSubService.getList();
	}
	public getTypeAccesMapById(): Observable<Map<number, TypeAcces>> {
		return this.typeAccesSubService.getMapById();
	}
	public getPaSteps(): Observable<DocumentPaSteps[]> {
		return this.documentPaStepsSubService.getList();
	}
}

angular
	.module('auroraApp')
	.service('staticTypeService', downgradeInjectable(StaticTypeService));